import { RECOMMENDED_PLAN_PARAM, type RecommendedPlanId } from '@/features/vias-acompanamiento/offerPaths'
import { ROUTES } from '@/navigation/constants'

export type RouterProfile = 'tool' | 'orden' | 'asesoria'

export type RouterRecommendation = {
    profile: RouterProfile
    /** Ponlo en orden plan to highlight on arrival. Only set for the `orden` profile. */
    plan?: RecommendedPlanId
}

type QuizOption = {
    label: string
    meta: string
}

export type QuizQuestion = {
    id: string
    title: string
    /** Small secondary line under the title. */
    hint?: string
    options: QuizOption[]
}

/** Landing each profile is sent to once the quiz is answered. */
export const ROUTER_DESTINATIONS: Record<RouterProfile, string> = {
    tool: ROUTES.home,
    orden: ROUTES.ponloEnOrden,
    asesoria: ROUTES.asesoria,
}

export const ROUTER_QUESTIONS: QuizQuestion[] = [
    {
        id: 'necesidad',
        title: '¿Qué necesitas ahora mismo?',
        options: [
            {
                label: 'Quiero una herramienta que me lo ponga fácil y rápido',
                meta: 'Lo controlo yo, desde el móvil',
            },
            {
                label: 'Necesito alguien que me ayude a hacerlo',
                meta: 'La herramienta + acompañamiento para usarla bien',
            },
            {
                label: 'Quiero que lo hagan por mí y yo estar en mi día a día',
                meta: 'Alguien se ocupa de los números y me guía para ganar más',
            },
        ],
    },
    {
        id: 'facturacion',
        title: '¿Cuánto factura tu negocio al mes?',
        hint: 'No compartimos estos datos con terceros',
        options: [
            { label: 'Hasta 15.000 €', meta: 'Negocio pequeño o recién abierto' },
            { label: 'Entre 15.000 y 40.000 €', meta: 'Funciona, pero se escapa margen' },
            { label: 'Más de 40.000 €', meta: 'Volumen alto o varios locales' },
        ],
    },
    {
        id: 'urgencia',
        title: '¿Cómo ves el negocio a día de hoy?',
        options: [
            {
                label: 'Ya gano dinero, solo quiero control',
                meta: 'Tener los números claros y a mano',
            },
            {
                label: 'Facturo bien, pero no gano lo que me gustaría',
                meta: 'Algo se me escapa y no sé qué es',
            },
            {
                label: 'Tengo que arreglarlo urgentemente',
                meta: 'Necesito ayuda ya',
            },
        ],
    },
]

/** Picked option of each question, by index. */
const NEED = { tool: 0, orden: 1, asesoria: 2 } as const
const REVENUE = { upTo15k: 0, from15to40k: 1, over40k: 2 } as const
const URGENCY = { control: 0, notEarningEnough: 1, urgent: 2 } as const

const BASE_PROFILE_BY_NEED: RouterProfile[] = ['tool', 'orden', 'asesoria']

/**
 * The first answer picks the base path until revenue is known. Then, in order:
 * - urgent → orden: Apaga el incendio from 15k (on-site, and the natural step into the advisory),
 *   Tipi Express below. Urgent visitors never land on the advisory waitlist.
 * - over 40k + "hazlo por mí" or "no gano lo que quiero" → asesoria: from there the profit gap pays the yearly fee
 *   back within the first year.
 * - from 15k → orden with Local Rentable; the advisory stays visible there as the anchor.
 * - up to 15k: tool when asked for, otherwise orden with Local Rentable.
 */
export function resolveRecommendation(answers: number[]): RouterRecommendation | null {
    const need = answers[0]
    if (need == null) return null

    const base = BASE_PROFILE_BY_NEED[need]
    if (!base) return null

    const revenue = answers[1]
    const urgency = answers[2]
    if (revenue == null) return { profile: base }

    const from15k = revenue >= REVENUE.from15to40k

    if (urgency === URGENCY.urgent) {
        return { profile: 'orden', plan: from15k ? 'apaga_incendio' : 'tipi_express' }
    }
    if (revenue === REVENUE.over40k && (need === NEED.asesoria || urgency === URGENCY.notEarningEnough)) {
        return { profile: 'asesoria' }
    }
    if (!from15k && need === NEED.tool) return { profile: 'tool' }

    return { profile: 'orden', plan: 'local_rentable' }
}

export function resolveProfile(answers: number[]): RouterProfile | null {
    return resolveRecommendation(answers)?.profile ?? null
}

/** Same-origin result URL that keeps UTM/click ids and adds the quiz answers. */
export function buildResultUrl(answers: number[], currentSearch: string): string {
    const recommendation = resolveRecommendation(answers)
    if (!recommendation) return ROUTES.home

    const { profile, plan } = recommendation
    const params = new URLSearchParams(currentSearch)
    params.set('perfil', profile)
    if (plan) params.set(RECOMMENDED_PLAN_PARAM, plan)
    else params.delete(RECOMMENDED_PLAN_PARAM)
    answers.forEach((optionIndex, questionIndex) => {
        params.set(`q_${ROUTER_QUESTIONS[questionIndex].id}`, String(optionIndex + 1))
    })

    return `${ROUTER_DESTINATIONS[profile]}?${params.toString()}`
}
